// --- Dependencies --------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import type { TyApiResponse, TyBranch } from "@/types";
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import ActionsBar from "@/UI/components/ActionsBar";
import BranchCard from "@/UI/cards/BranchCard";
import Skeleton from "@/UI/atoms/Skeleton";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import FormBranchPopup from "@/UI/popups/FormBranchPopup";
import MoreActionsPopup from "@/UI/popups/MoreActionsPopup";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

export default function SubscriptionBranchesScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const { addComp, removeComp } = usePopup();
    const { canEdit } = usePermission();

    const { data, loading, error, invoke } = useFetch<TyApiResponse<{ branches: TyBranch[]; totalAllowed: number }>>("/branches");

    const branches = data?.data?.branches ?? [];
    const totalAllowed = data?.data?.totalAllowed ?? branches.length;
    const unconfiguredCount = Math.max(0, totalAllowed - branches.length);
    const unconfiguredSlots = Array.from({ length: unconfiguredCount }, (_, i) => branches.length + i);

    // --- Delete branch ---------------------------------------------------------------------------

    const [deletingId, setDeletingId] = useState<string | null>(null);

    const { data: deleteData, invoke: invokeDelete } = useFetch<TyApiResponse<Record<string, never>>>(
        `/subscriptions/branches/${deletingId ?? "x"}`,
        { method: "DELETE" },
        false
    );

    useEffect(() => {
        if (!deletingId) return;
        invokeDelete();
    }, [deletingId]);

    useEffect(() => {
        if (!deleteData?.success) return;
        setDeletingId(null);
        invoke();
    }, [deleteData]);

    // --- Handlers --------------------------------------------------------------------------------

    const openBranchForm = (editBranchId?: string) => {
        addComp({
            name: "form-branch",
            Component: FormBranchPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: { onSuccess: invoke, editBranchId, pathPrefix: "/subscriptions/branches" },
        });
    };

    const handleDeletePrompt = (branch: TyBranch) => {
        removeComp("confirm-delete-branch");
        addComp({
            name: "confirm-delete-branch",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: t.branches.deleteConfirmMessage,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.branches.deleteConfirmLabel,
                onConfirm: () => setDeletingId(branch._id),
            },
        });
    };

    const handleMore = (branch: TyBranch, e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const name = `more-actions-${branch._id}`;
        removeComp(name);
        addComp({
            name,
            Component: MoreActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
            details: {
                actions: [
                    { label: t.branches.editAction, icon: "/icons/edit-icon.svg", handler: () => openBranchForm(branch._id) },
                    { label: t.branches.deleteAction, icon: "/icons/delete-icon.svg", handler: () => handleDeletePrompt(branch), danger: true },
                ],
            },
        });
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="sub-branches-screen">
            <ActionsBar title={t.subscriptions.branchConfigTitle} onBack={() => navigate("/subscriptions")} />

            <div className="sub-branches-screen-content">
                {loading && (
                    <div className="sub-branches-screen-grid">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="sub-branches-screen-skeleton" />
                        ))}
                    </div>
                )}

                {error && <p className="sub-branches-screen-message sub-branches-screen-message--error">{error}</p>}

                {!loading && !error && branches.length === 0 && unconfiguredCount === 0 && (
                    <div className="sub-branches-screen-empty">
                        <div className="sub-branches-screen-empty-icon">
                            <img src="/icons/side-bar/branches-icon-dark.svg" alt="" width={32} height={32} />
                        </div>
                        <div className="sub-branches-screen-empty-text">
                            <p className="sub-branches-screen-empty-title">{t.branches.emptyTitle}</p>
                            <p className="sub-branches-screen-empty-subtitle">{t.branches.emptySubtitle}</p>
                        </div>
                    </div>
                )}

                {!loading && !error && (branches.length > 0 || unconfiguredCount > 0) && (
                    <div className="sub-branches-screen-grid">
                        {branches.map((branch, i) => (
                            <BranchCard
                                key={branch._id}
                                variant="configured"
                                branch={branch}
                                index={i}
                                onMore={canEdit("subscriptions") ? (e) => handleMore(branch, e) : undefined}
                            />
                        ))}
                        {unconfiguredSlots.map((slotIndex) => (
                            <BranchCard
                                key={`slot-${slotIndex}`}
                                variant="unconfigured"
                                index={slotIndex}
                                onAdd={canEdit("subscriptions") ? () => openBranchForm() : undefined}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
